// src/components/check-ins/WeeklyFinancialCheckIn.jsx
import React, { useEffect, useState } from "react";
import {
  calculateClosingCash,
  calculateCumulativeCashPreview,
  canEditWeeklyCheckIn,
  formatWeeklyCheckInDate,
  getCurrentSAWeekStart,
  getMyWeeklyCheckInContext,
  getWeeklyCheckInInitialValues,
  getWeeklyCheckInReferenceDate,
  saveMyWeeklyFinancialCheckIn,
} from "../../lib/check-ins/weeklyCheckIn";

const FIELDS = [
  { key: "opening_cash_balance", label: "What was your cash balance at the start of the week?" },
  { key: "total_sales", label: "How much sales did you make this week?" },
  { key: "cash_sales_received", label: "How much of these sales did you receive in cash?" },
  { key: "total_spent", label: "How much did you spend this week?" },
  { key: "cash_spent", label: "How much of this spending was in cash?" },
];

function money(value) {
  return Number(value || 0).toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
}

export default function WeeklyFinancialCheckIn({ open, onClose, onSubmitted, weekStart: requestedWeekStart }) {
  const [values, setValues] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);
  const [savedCumulative, setSavedCumulative] = useState(null);
  const weekStart = requestedWeekStart || getCurrentSAWeekStart();

  useEffect(() => {
    if (!open) return;
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError("");
        setSuccess(false);
        setSavedRecord(null);
        setSavedCumulative(null);
        setContext(null);
        setValues(null);
        const result = await getMyWeeklyCheckInContext(weekStart);
        if (!alive) return;
        setContext(result);
        setValues(getWeeklyCheckInInitialValues(result.existing, result.previous));
      } catch (err) {
        if (alive) setError(err.message || "Could not load your check-in.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [open, weekStart]);

  if (!open) return null;

  const dates = context?.dates;
  const status = context?.status;
  const isCompleted = Boolean(context?.existing?.submitted_at);

const isReadOnly =
  isCompleted ||
  !canEditWeeklyCheckIn(
    weekStart,
    getWeeklyCheckInReferenceDate()
  );
  const allValid = values &&
    FIELDS.every(({ key }) => {
      const text = String(values[key] ?? "").trim();
      return text !== "" && Number.isFinite(Number(text));
    }) &&
    Number(values.total_sales) >= 0 &&
    Number(values.cash_sales_received) >= 0 &&
    Number(values.total_spent) >= 0 &&
    Number(values.cash_spent) >= 0 &&
    Number(values.cash_sales_received) <= Number(values.total_sales) &&
    Number(values.cash_spent) <= Number(values.total_spent);

  const closing = values ? calculateClosingCash(values) : 0;
  // Calculate the cumulative preview without counting
  // the current reporting week twice.
  // Never count both the old saved record and the new draft for the same week.
  const previewCumulative =
  context && allValid && (!isReadOnly || context.existing)
    ? calculateCumulativeCashPreview(
        context.submissions,
        weekStart,
        closing
      )
    : null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (
  !allValid ||
  saving ||
  Boolean(context?.existing?.submitted_at) ||
  !canEditWeeklyCheckIn(
    weekStart,
    getWeeklyCheckInReferenceDate()
  )
) {
  return;
}
    try {
      setSaving(true);
      setError("");
      const saved = await saveMyWeeklyFinancialCheckIn(weekStart, values);
      if (!saved?.id || saved.week_start !== weekStart) {
        throw new Error("The database did not confirm this check-in.");
      }
      setSavedRecord(saved);
      setSavedCumulative(
  calculateCumulativeCashPreview(
    context?.submissions || [],
    weekStart,
    saved.closing_cash_balance
  )
);
      setSuccess(true);
      onSubmitted?.(saved);
    } catch (err) {
      setError(err.message || "Could not save your check-in.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-[430px] overflow-y-auto rounded-[24px] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase text-[#FF914D]">Gwin financial check-in</p>
            <h2 className="mt-1 text-[23px] font-black text-[#222222]">Weekly Cash Check-In</h2>
            <p className="mt-2 text-[12px] text-[#757575]">Week starting {formatWeeklyCheckInDate(weekStart)}</p>
            {dates && (
              <p className="mt-1 text-[11px] text-[#757575]">
                Due Friday {formatWeeklyCheckInDate(dates.dueDate)}{" · "}
                Final deadline Wednesday {formatWeeklyCheckInDate(dates.graceEnd)}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose}
            className="rounded-full bg-[#F3F3F3] px-3 py-1 text-xl text-[#777777]"
            aria-label="Close check-in">×</button>
        </div>

        {loading ? (
          <p className="mt-8 text-sm text-[#757575]">Loading your financial information...</p>
        ) : success ? (
          <div className="mt-8 text-center">
            <div className="text-4xl">✓</div>
            <h3 className="mt-3 text-xl font-bold">Check-in saved!</h3>
            <p className="mt-2 text-sm text-[#757575]">
              Your ending cash balance is {money(savedRecord?.closing_cash_balance)}.
            </p>
            <p className="mt-3 text-sm font-bold text-[#222222]">
              Cumulative total of weekly ending balances: {money(savedCumulative)}
            </p>
            <button type="button" onClick={onClose}
              className="mt-6 w-full rounded-xl bg-[#FF914D] py-3 font-bold text-white">DONE</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {isReadOnly && (
              <div className="rounded-xl bg-[#FFF0E6] p-4 text-xs text-[#8A431D]">
                {isCompleted
  ? "You've already completed this week's financial check-in. Your submitted answers are locked."
  : status === "missed"
  ? "This check-in was missed. The Wednesday submission deadline has passed."
  : status === "upcoming"
  ? `This check-in becomes available on Friday ${formatWeeklyCheckInDate(dates?.dueDate)}.`
  : "The submission deadline has passed."}
              </div>
            )}
            {context?.previous && !context?.existing && (
              <p className="rounded-xl bg-[#F4F4F6] p-3 text-[11px] text-[#757575]">
                We've prefilled your last completed check-in's answers. Please update the amounts for this week.
              </p>
            )}
            {values && FIELDS.map((field) => (
              <label key={field.key} className="block">
                <span className="mb-2 block text-[12px] font-bold text-[#222222]">{field.label}</span>
                <div className="flex overflow-hidden rounded-xl border border-[#DDDDDD]">
                  <span className="flex items-center bg-[#F4F4F6] px-4 text-sm font-bold">R</span>
                  <input type="number" step="0.01"
                    min={field.key === "opening_cash_balance" ? undefined : "0"}
                    value={values[field.key]} disabled={isReadOnly}
                    onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                    placeholder="0.00"
                    className="h-12 w-full min-w-0 px-3 text-sm outline-none disabled:bg-[#F5F5F5]" />
                </div>
              </label>
            ))}
            {values && (
              <div className="rounded-[18px] bg-[#FFF0E6] p-5">
                <p className="text-[11px] font-bold text-[#9B5835]">Your calculated ending cash balance</p>
                <p className="mt-2 text-[28px] font-black text-[#222222]">
                  {allValid ? money(closing) : "—"}
                </p>
                <p className="mt-2 text-[10px] text-[#757575]">Opening cash + cash received − cash spent</p>
                {previewCumulative !== null && (
                  <div className="mt-4 border-t border-[#E8C6B2] pt-3">
                    <p className="text-[11px] font-bold text-[#9B5835]">
                      Cumulative total of weekly ending balances {isReadOnly ? "" : "(preview)"}
                    </p>
                    <p className="mt-1 text-[23px] font-black text-[#222222]">
                      {money(previewCumulative)}
                    </p>
                    <p className="mt-1 text-[10px] text-[#757575]">
                      Sum of completed weeks' ending balances, including this week's displayed figures.
                    </p>
                  </div>
                )}
              </div>
            )}
            {!!error && <p className="rounded-xl bg-red-50 p-3 text-xs text-red-700">{error}</p>}
            {!isReadOnly && values && (
              <button type="submit" disabled={!allValid || saving}
                className="w-full rounded-xl bg-[#FF914D] py-3.5 text-sm font-black text-white disabled:opacity-50">
                {saving ? "SAVING..." : "SUBMIT CHECK-IN"}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
